import { AppProps } from "./types";
import { FontFamilyValues } from "./element/types";
import {
  BackgroundIcon,
  BasketballIcon,
  DarkModeIcon,
  DiagramIcon,
  DotsIcon,
  DrawioIcon,
  EraserIcon,
  FootballIcon,
  GoogleDocsIcon,
  GoogleSheetIcon,
  GoogleSlidesIcon,
  GustureIcon,
  LineIcon,
  NoteIcon,
  PenIcon,
  PencilIcon,
  RectangleIcon,
  SelectionIcon,
  StrokeStyleDashedIcon,
  TextIcon,
  YoutubeIcon,
  StemIcon,
  PersonalBoardIcon,
  SchoolIcon,
} from "./components/icons";

export const APP_NAME = "Collab";

export const DRAGGING_THRESHOLD = 10; // px
export const LINE_CONFIRM_THRESHOLD = 8; // px
export const ELEMENT_SHIFT_TRANSLATE_AMOUNT = 5;
export const ELEMENT_TRANSLATE_AMOUNT = 1;
export const TEXT_TO_CENTER_SNAP_THRESHOLD = 30;
export const SHIFT_LOCKING_ANGLE = Math.PI / 12;
export const CURSOR_TYPE = {
  TEXT: "text",
  CROSSHAIR: "crosshair",
  GRABBING: "grabbing",
  GRAB: "grab",
  POINTER: "pointer",
  MOVE: "move",
  AUTO: "",
};
export const POINTER_BUTTON = {
  MAIN: 0,
  WHEEL: 1,
  SECONDARY: 2,
  TOUCH: -1,
} as const;

export enum EVENT {
  COPY = "copy",
  PASTE = "paste",
  CUT = "cut",
  KEYDOWN = "keydown",
  KEYUP = "keyup",
  MOUSE_MOVE = "mousemove",
  RESIZE = "resize",
  UNLOAD = "unload",
  FOCUS = "focus",
  BLUR = "blur",
  DRAG_OVER = "dragover",
  DROP = "drop",
  GESTURE_END = "gestureend",
  BEFORE_UNLOAD = "beforeunload",
  GESTURE_START = "gesturestart",
  GESTURE_CHANGE = "gesturechange",
  POINTER_MOVE = "pointermove",
  POINTER_UP = "pointerup",
  STATE_CHANGE = "statechange",
  WHEEL = "wheel",
  TOUCH_START = "touchstart",
  TOUCH_END = "touchend",
  TOUCH_MOVE = "touchmove",
  HASHCHANGE = "hashchange",
  VISIBILITY_CHANGE = "visibilitychange",
  SCROLL = "scroll",
  // custom events
  IMAGO_LINK = "imago-link",
  DRAG_START = "dragstart",
}

export const ENV = {
  TEST: "test",
  DEVELOPMENT: "development",
};

export const CLASSES = {
  SHAPE_ACTIONS_MENU: "App-menu__left",
};

// 1-based in case we ever do `if(element.fontFamily)`
export const FONT_FAMILY = {
  Helvetica: 1,
  Arial: 2,
  ArialBlack: 3,
  Verdana: 4,
  Tahoma: 5,
  TrebuchetMS: 6,
  Impact: 7,
  GillSans: 8,
  TimesNewRoman: 9,
  Georgia: 10,
  Palatino: 11,
  Baskerville: 12,
  AndaleMono: 13,
  Courier: 14,
  LucidaConsole: 15,
  Monaco: 16,
  BradleyHand: 17,
  BrushScriptMT: 18,
  Luminari: 19,
  ComicSansMS: 20,
  Optima: 21,
  Didot: 22,
  AmericanTypewriter: 23,
};

export type FontFamlilyInfo = {
  label: string;
  info: number;
};


export const FONT_FAMILY_DETAIL = {

  Arial: {
    label: "Arial",
    info: "Arial, Helvetica"
  },
  Palatino: {
    label: "Palatino",
    info: "palatino, serif"
  },
  ArialBlack: {
    label: "Arial Black",
    info: "Arial black, sans-serif"
  },
  Verdana: {
    label: "Verdana",
    info: "'Verdana, sans-serif"
  },
  Tahoma: {
    label: "Tahoma",
    info: "Tahoma, sans-serif"
  },
  TrebuchetMS: {
    label: "Trebuchet MS",
    info: "'Trebuchet MS', sans-serif"
  },
  Impact: {
    label: "Impact",
    info: "Impact, sans-serif"
  },
  GillSans: {
    label: "Gill Sans",
    info: "'Gill Sans', sans-serif"
  },
  Helvetica: {
    label: "Helvetica",
    info: "Helvetica, sans-serif"
  },
  TimesNewRoman: {
    label: "Times New Roman",
    info: "'Times New Roman', serif"
  },
  Georgia: {
    label: "Georgia",
    info: "Georgia, serif"
  },

  Baskerville: {
    label: "Baskerville",
    info: "baskerville, serif"
  },
  AndaleMono: {
    label: "Andalé Mono",
    info: "Andale Mono, monospace"
  },
  Courier: {
    label: "Courier",
    info: "Courier, monospace"
  },
  LucidaConsole: {
    label: "Lucida Console",
    info: "Lucida Console, monospace"
  },
  Monaco: {
    label: "Monaco",
    info: "monaco, monospace"
  },
  BradleyHand: {
    label: "Bradley Hand",
    info: "bradley hand, cursive"
  },

  Luminari: {
    label: "Luminari",
    info: "Luminari, fantasy"
  },
  ComicSansMS: {
    label: "Comic Sans MS",
    info: "Comic Sans MS, cursive"
  },
  Didot: {
    label: "Didot",
    info: "Didot, serif"
  },
  BrushScriptMT: {
    label: "Brush Script MT",
    info: "Brush Script MT, cursive"
  },
  Optima: {
    label: "Optima",
    info: "Optima, sans-serif"
  },
  AmericanTypewriter: {
    label: "American Typewriter",
    info: "American Typewriter, serif"
  },

};

export const THEME = {
  LIGHT: "light",
  DARK: "dark",
};

export const WINDOWS_EMOJI_FALLBACK_FONT = "Segoe UI Emoji";

export const DEFAULT_FONT_SIZE = 20;
export const DEFAULT_FONT_FAMILY: FontFamilyValues = FONT_FAMILY.Arial;
export const DEFAULT_TEXT_ALIGN = "left";
export const DEFAULT_VERTICAL_ALIGN = "top";
export const DEFAULT_VERSION = "{version}";

export const CANVAS_ONLY_ACTIONS = ["selectAll"];

export const GRID_SIZE = 20; // TODO make it configurable?

export const MIME_TYPES = {
  clb: "application/vnd.clb+json",
  clbnote: "application/vnd.clbnote+json",
  imagolib: "application/vnd.imagolib+json",
  json: "application/json",
  svg: "image/svg+xml",
  "imago.svg": "image/svg+xml",
  png: "image/png",
  "imago.png": "image/png",
  jpg: "image/jpeg",
  zip: "application/zip",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  ico: "image/x-icon",
  binary: "application/octet-stream",
  pdf: "application/pdf",
  doc: "application/doc",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  "imago.pdf": "application/pdf",
} as const;

export const EXPORT_DATA_TYPES = {
  clb: "clb",
  imagoClipboard: "imago/clipboard",
  imagoLibrary: "imagolib",
} as const;


export const BOARD_MODE = {
  warRoom: 1,
  moderator: 2,
  classRoom: 3,
} as const;

export const EXPORT_SOURCE =
  (window as any).IMAGO_EXPORT_SOURCE || window.location.origin;

// time in milliseconds
export const IMAGE_RENDER_TIMEOUT = 500;
export const TAP_TWICE_TIMEOUT = 300;
export const TOUCH_CTX_MENU_TIMEOUT = 500;
export const TITLE_TIMEOUT = 10000;
export const VERSION_TIMEOUT = 30000;
export const SCROLL_TIMEOUT = 100;
export const ZOOM_STEP = 0.1;
export const MIN_ZOOM = 0.1;
export const HYPERLINK_TOOLTIP_DELAY = 300;
export const AUTO_SAVE_SCENE_TIMEOUT = 60000;

// Report a user inactive after IDLE_THRESHOLD milliseconds
export const IDLE_THRESHOLD = 60_000;
// Report a user active each ACTIVE_THRESHOLD milliseconds
export const ACTIVE_THRESHOLD = 3_000;

export const MODES = {
  VIEW: "viewMode",
  ZEN: "zenMode",
  GRID: "gridMode",
};



export const URL_QUERY_KEYS = {
  addLibrary: "addLibrary",
} as const;

export const URL_HASH_KEYS = {
  addLibrary: "addLibrary",
} as const;

export const DEFAULT_UI_OPTIONS: AppProps["UIOptions"] = {
  canvasActions: {
    changeViewBackgroundColor: true,
    clearCanvas: true,
    export: { saveFileToDisk: true },
    loadScene: true,
    saveToActiveFile: true,
    toggleTheme: null,
    saveAsImage: true,
  },
};

// breakpoints
// -----------------------------------------------------------------------------
// sm screen
export const MQ_SM_MAX_WIDTH = 640;
// md screen
export const MQ_MAX_WIDTH_PORTRAIT = 730;
export const MQ_MAX_WIDTH_LANDSCAPE = 960;
export const MQ_MAX_HEIGHT_LANDSCAPE = 500;
// sidebar
export const MQ_RIGHT_SIDEBAR_MIN_WIDTH = 1229;
// -----------------------------------------------------------------------------



export const MAX_DECIMALS_FOR_SVG_EXPORT = 2;

export const EXPORT_SCALES = [1, 2, 3];
export const DEFAULT_EXPORT_PADDING = 10; // px

export const DEFAULT_MAX_IMAGE_WIDTH_OR_HEIGHT = 1440;

export const ALLOWED_IMAGE_MIME_TYPES = [
  MIME_TYPES.png,
  MIME_TYPES.jpg,
  MIME_TYPES.svg,
  MIME_TYPES.gif,
  MIME_TYPES.webp,
  MIME_TYPES.bmp,
  MIME_TYPES.ico,
] as const;

export const MAX_ALLOWED_FILE_BYTES = 2 * 1024 * 1024;

export const SVG_NS = "http://www.w3.org/2000/svg";

export const ENCRYPTION_KEY_BITS = 128;

export const VERSIONS = {
  imago: 2,
  imagoLibrary: 2,
} as const;

export const BOUND_TEXT_PADDING = 5;

export const VERTICAL_ALIGN = {
  TOP: "top",
  MIDDLE: "middle",
  BOTTOM: "bottom",
};

export const TEXT_ALIGN = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
};

export const ELEMENT_READY_TO_ERASE_OPACITY = 20;

export const COOKIES = {
  AUTH_STATE_COOKIE: "excplus-auth",
  AUTH: "authb",
  LICENCE: "licence",
  FLASH4AUTH: "flash4auth",
} as const;

/** key containt id of precedeing elemnt id we use in reconciliation during
 * collaboration */
export const PRECEDING_ELEMENT_KEY = "__precedingElement__";

export const BACKGROUND_ICONS = {
  background: BackgroundIcon,
  "dark-mode": DarkModeIcon,
  "dot-grid": DotsIcon,
  "line-grid": LineIcon,
  "line-dash-grid": StrokeStyleDashedIcon,
};

export const TOOLBAR_GROUPS = {
  pointer: SelectionIcon,
  pencil: PencilIcon,
  square: DiagramIcon,
  text: TextIcon,
  note: NoteIcon,
  personalBoard: PersonalBoardIcon,
  "eraser-one": EraserIcon,
  gusture: GustureIcon,
};

export const AI_SERVICE_HOST = process.env.AI_SERVICE_HOST || "https://ai.collabnext.imago.buzz";

export const API_URL = {
  host: window.location.origin,
  aiHost: "",
  saveUserScene: "/api/v1/user-page",
  getUserScene: "/api/v1/user-page",
  dowloadTemplate: "/api/v1/download/templates",
  getScenePrefix: "/api/v1/getScene",
  saveScenePrefix: "/api/v1/saveScene",
  savePersonDataPrefix: "/api/v1/savePersonalBoardData",
  getPersonDataPrefix: "/api/v1/getPersonalBoardData",
  scanUploadImage: "/api/v1/board-images",
  uploadImageDownload: "/api/v1/download/upload-images",
  saveDownFile: "/api/v1/saveDownFile",
  saveRoom: "/api/v1/saveRoom",
  getLicence: "/api/v1/room/licence",
  saveFile: "/api/v1/saveFile",
  saveRoomLink: "/api/v1/saveRoomLink",
  batchSaveRoomLinks: "/api/v1/batchSaveRoomLinks",
  getRoomLinks: "/api/v1/getRoomLinks",
  delRoomLink: "/api/v1/delRoomLink",
  saveStickyNote: "/api/v1/saveStickyNote",
  batchSaveStickyNotes: "/api/v1/batchSaveStickyNotes",
  replaceStickyNote: "/api/v1/replaceStickyNote",
  getRoomStickyNotes: "/api/v1/getRoomStickyNotes",
  delStickyNote: "/api/v1/delStickyNote",
  getRoom: "/api/v1/getRoom",
  getRoomByGmId: "/api/v1/getRoomByGmId",
  aiChat: "/aiapi/v1/chat",
  aiAnalyzeImage: "/aiapi/v1/image/analyze",
  aiSummarize: "/aiapi/v1/summarize",
  aiGenerateStory: "/aiapi/v1/story",
  aiUploadImage: "/aiapi/v1/image",
  aiGenerateImg: "/api/v1/aiagent-api/generate-image-prompt",
  aiShowFile: "/api/v1/aiagent-api/file",
  aiTextToSpeech: "/api/v1/aiagent-api/text-to-speech",
  aiCompletionMessages: "/api/v1/aiagent-api/completion-messages",
  aiLearningCompanion: "/api/v1/aiagent-api/learning-companion",
  aiVocabularyGenerator: "/api/v1/aiagent-api/vocabulary-generator",
  aiLessonPlan: "/api/v1/aiagent-api/lesson-plan",
  aiVoice: "/aiapi/v1.2/tts",
  aiDiagram: "/aiapi/v1/diagram",
  aiUploadFile: "/aiapi/v1/translate/upload",
  aiTranslateFile: "/aiapi/v1/translate/translate-file",
  aiSocialStory: "/api/v1/aiagent-api/social-story",
  aiSportsPractice: "/api/v1/aiagent-api/sports-practice",
  aiTeacherJoke: "/api/v1/aiagent-api/teacher-joke",
  aiMathStory: "/api/v1/aiagent-api/math-story",
  aiRubricGenerator: "/api/v1/aiagent-api/rubric-generator",
  aiWorksheetGenerator: "/api/v1/aiagent-api/worksheet-generator",
  aiScienceTeaching: "/api/v1/aiagent-api/science-teaching",
  aiBlogGenerator: "/api/v1/aiagent-api/blog-title-generator",
  aiTextLeveler: "/api/v1/aiagent-api/text-leveler",
  aiGrammerChecker: "/api/v1/aiagent-api/grammar-checker",
  aiResearchAgent: "/api/v1/aiagent-api/research-agent",
  aiHistorianQuestion: "/api/v1/aiagent-api/historian_question",
  aiLanguageTutor: "/api/v1/aiagent-api/language_tutor",
  aiStrategicConsulting: "/aiapi/v1/strategic-consulting",
  aiEducationalFileSummariser: '/aiapi/v1/educational-file-summariser',
  aiScienceLabDesigner: '/aiapi/v1/science-lab-designer',
  aiAcademicContent: '/aiapi/v1/academic-content',
  aiYoutubeDataAnalysis: '/aiapi/v1/youtube-data-analysis',
  aiLessonMinutesSummary: '/aiapi/v1/lesson-minutes-summary',
  aiReportCardComment: '/aiapi/v1/report-card-comment',
  aiProjectBasedLearning: '/aiapi/v1/project-based-learning',
  aiSatElaPracticeTest: '/aiapi/v1/sat-ela-practice-test',
  aiBlogInline: '/aiapi/v1/blog-inline',
  aiAutomaticSpeechRecognition: "/aiapi/v1/asr"
}

export const LIBRARY_LIST_LIMIT = 28;

export const GRID_STYLE = {
  "dot-grid": "dot",
  "line-grid": "line",
  "line-dash-grid": "lineDash",
  "row-grid": "row",
  "five-line-grid": "fiveLine",
  "music-score-grid": "musicScore",
  "mizi-grid": "mizi",
};

export const EmbedType = {
  youtube: { title: "Youtube", icon: YoutubeIcon },
  googleDocs: { title: "Google Docs", icon: GoogleDocsIcon },
  googleSheet: { title: "Google Sheet", icon: GoogleSheetIcon },
  googleSlides: { title: "Google Slides", icon: GoogleSlidesIcon },
  drawio: { title: " Flow Chart", icon: DrawioIcon },
  stem: { Title: "Stem", icon: StemIcon },
  school: { Title: "School", icon: SchoolIcon }
};

export const RUBBER_SIZE = {
  width: 120,
  height: 160
}

export const FLASH_COLLAB = {
  NO_AUTH_URL: "flashcollab.imago.us",
  AUTH_URL: "flashpro.imago.us",
  REQUEST_FLASH_AUTH_TOKEN: "/api/v1/token4flash",
  ENABLE_AUTH: true
}